import { useState } from "react";
import { Alert } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useUploadStoryVideoMutation } from "Api/Mutations/Stories/useUploadStoryVideoMutation";
import {
  MediaUploadField,
  type MediaUploadIssue,
  getFileNameFromUrl,
  getMediaUploadIssue,
} from "Components/Stories/MediaUploadField";
import {
  ACCEPTED_VIDEO_TYPES,
  MAX_VIDEO_DURATION_SECONDS,
  MAX_VIDEO_FILE_SIZE_MB,
} from "Components/Stories/storyMediaLimits";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Stories.VideoUpload;

type ValidationIssue = MediaUploadIssue | "tooLong";

/** Reads the duration from the file's metadata without uploading it. */
const readVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(probe.duration);
    };
    probe.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to read video metadata"));
    };
    probe.src = objectUrl;
  });

type Props = {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
};

/** Video upload with a client-side duration gate; uploads through our API to our blob storage. */
export const VideoUploadField = ({ label, hint, value, onChange }: Props) => {
  const { t } = useResource();
  // The picked file's name, kept while this field owns the value; null after a reload or failure.
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [validationIssue, setValidationIssue] =
    useState<ValidationIssue | null>(null);
  const [isProbing, setIsProbing] = useState(false);

  const {
    mutate: uploadVideo,
    isPending: isUploading,
    isError,
  } = useUploadStoryVideoMutation({
    onSuccess: onChange,
    onError: () => setUploadedFileName(null),
  });

  const validateAndUpload = async (file: File) => {
    const issue = getMediaUploadIssue(
      file,
      ACCEPTED_VIDEO_TYPES,
      MAX_VIDEO_FILE_SIZE_MB,
    );
    setValidationIssue(issue);
    if (issue !== null) {
      return;
    }
    setIsProbing(true);
    try {
      const duration = await readVideoDuration(file);
      if (duration > MAX_VIDEO_DURATION_SECONDS) {
        setValidationIssue("tooLong");
        return;
      }
      setUploadedFileName(file.name);
      uploadVideo(file);
    } catch {
      // Unreadable metadata — let the server-side validation reject it.
      setUploadedFileName(file.name);
      uploadVideo(file);
    } finally {
      setIsProbing(false);
    }
  };

  return (
    <MediaUploadField
      label={label}
      hint={hint}
      acceptedTypes={ACCEPTED_VIDEO_TYPES}
      isBusy={isProbing || isUploading}
      preview={
        value ? (
          // biome-ignore lint/a11y/useMediaCaption: story videos have no caption tracks
          <video
            src={value}
            controls
            muted
            style={{ width: "100%", height: "100%", display: "block" }}
          />
        ) : null
      }
      fileName={
        value ? (uploadedFileName ?? getFileNameFromUrl(value)) : undefined
      }
      emptyIcon={<VideocamIcon color="action" />}
      promptText={t(COMPONENT_RESOURCES.Prompt)}
      alerts={
        <>
          {validationIssue === "invalidType" && (
            <Alert severity="warning">
              {t(COMPONENT_RESOURCES.InvalidType)}
            </Alert>
          )}
          {validationIssue === "tooLarge" && (
            <Alert severity="warning">
              {t(COMPONENT_RESOURCES.TooLarge, {
                megabytes: MAX_VIDEO_FILE_SIZE_MB,
              })}
            </Alert>
          )}
          {validationIssue === "tooLong" && (
            <Alert severity="warning">
              {t(COMPONENT_RESOURCES.TooLong, {
                seconds: MAX_VIDEO_DURATION_SECONDS,
              })}
            </Alert>
          )}
          {isError && (
            <Alert severity="error">{t(COMPONENT_RESOURCES.Error)}</Alert>
          )}
        </>
      }
      onFile={validateAndUpload}
      onClear={() => {
        setUploadedFileName(null);
        onChange("");
      }}
    />
  );
};
